import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HeartPulse, Mail, Lock, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../lib/utils';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await login(credentials.email, credentials.password);
      if (['admin', 'admin_general', 'socio_comercial'].includes(userData.role)) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Error al iniciar sesión'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#3b82f6]/30 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#14b8a6]/30 rounded-full blur-3xl" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        <Link to="/system/medicina" className="flex items-center justify-center gap-2 mb-8">
          <HeartPulse className="w-10 h-10 text-[#14b8a6]" />
          <span className="text-3xl font-bold text-white">Punto Cero Salud</span>
        </Link>

        <div className="backdrop-blur-xl bg-white/5 rounded-3xl p-8 border border-white/20 shadow-2xl">
          <h1 className="text-3xl font-bold text-white mb-2">Bienvenido de vuelta</h1>
          <p className="text-white/60 mb-6">Ingrese sus credenciales para acceder al panel</p>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-2">Correo electrónico</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input type="email" value={credentials.email} onChange={(e) => setCredentials({ ...credentials, email: e.target.value })} className="bg-white/10 border-white/20 text-white pl-10" placeholder="profesional@ejemplo.com" required data-testid="login-email" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-white/80 mb-2">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input type="password" value={credentials.password} onChange={(e) => setCredentials({ ...credentials, password: e.target.value })} className="bg-white/10 border-white/20 text-white pl-10" placeholder="••••••••" required data-testid="login-password" />
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] hover:shadow-[0_10px_30px_rgba(20,184,166,0.3)] text-white font-bold py-6" data-testid="login-submit">
              {loading ? 'Ingresando...' : 'Iniciar Sesión'}
              {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
            </Button>
          </form>

          <p className="text-center text-white/60 mt-6">
            ¿No tienes cuenta?{' '}
            <Link to="/register?vertical=salud" className="text-[#14b8a6] hover:underline font-semibold">Regístrate gratis</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
